import { type ContractorBadgeVerificationListData } from "@clipboard-health/contract-backend-main";
import { useWorkplaceBadge } from "@src/appV2/redesign/Workplace/hooks/useWorkplaceBadge";

import { useGetContractorBadgeVerification } from "../api/useGetContractorBadgeVerification";
import { isVerificationComplete } from "../utils/isVerificationComplete";
import { useIsBadgeVerificationClockInEnabled } from "./useIsBadgeVerificationClockInEnabled";

interface UseIsBadgeVerificationRequiredParams {
  shiftId: string;
  workplaceId: string | undefined;
}

interface UseIsBadgeVerificationRequiredResult {
  /**
   * Whether badge verification feature is enabled for this shift.
   */
  isEnabled: boolean;
  /**
   * Whether badge verification is required (not yet completed or skipped).
   * This is false if verification is completed (uploadedAt or skippedAt is set).
   */
  isRequired: boolean;
  /**
   * Whether the workplace has a badge verification override that enables the redesign flow.
   */
  hasBadgeVerificationOverride: boolean;
  /**
   * Whether the verification status is being loaded.
   */
  isLoading: boolean;
  /**
   * Error from fetching verification status, if any.
   */
  error: unknown;
  /**
   * The verification record if it exists.
   */
  verification: ContractorBadgeVerificationListData | undefined;
}

/**
 * Hook to check if badge verification is required for a shift.
 *
 * Badge verification is required when:
 * - Feature flag is enabled AND
 * - Workplace has independentContractorBadge enabled AND
 * - No verification record exists OR verification is not complete
 *
 * @param params.shiftId - The shift ID to check verification for
 * @param params.workplaceId - The workplace ID to check badge requirement for
 */
export function useIsBadgeVerificationRequired(
  params: UseIsBadgeVerificationRequiredParams
): UseIsBadgeVerificationRequiredResult {
  const { shiftId, workplaceId } = params;

  const isFeatureFlagEnabled = useIsBadgeVerificationClockInEnabled();
  const {
    isEnabled: isWorkplaceBadgeEnabled,
    isEnforced: isWorkplaceBadgeEnforced,
    hasBadgeVerificationOverride,
    isLoading: isWorkplaceLoading,
    error: workplaceError,
  } = useWorkplaceBadge(workplaceId);

  // Fetch verification in parallel with workplace (don't wait for workplace result)
  const {
    data,
    isLoading: isVerificationLoading,
    error: verificationError,
  } = useGetContractorBadgeVerification(shiftId, { enabled: isFeatureFlagEnabled });

  // Feature is enabled when flag is on AND workplace has badges enabled AND enforced
  const isEnabled = isFeatureFlagEnabled && isWorkplaceBadgeEnabled && isWorkplaceBadgeEnforced;

  const verification = data?.data[0];
  const isLoading = isFeatureFlagEnabled && (isWorkplaceLoading || isVerificationLoading);

  // While loading, default to required when the feature flag is enabled.
  // This prevents a race condition where the clock-in gate opens before
  // workplace badge settings have loaded — without this, isEnabled is false
  // during loading (because useWorkplaceBadge defaults to false), which
  // makes isRequired false and lets the worker bypass the gate.
  const isRequired = isLoading || (isEnabled && !isVerificationComplete(verification));

  return {
    isEnabled,
    isRequired,
    hasBadgeVerificationOverride,
    isLoading,
    error: workplaceError ?? verificationError,
    verification,
  };
}
